import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getData } from "../../services/dataService";
import "../../assets/styles/bootstrap.css";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { FloatingLabel } from "react-bootstrap";
import { MINIMUM_YEAR_THAT_CAN_BE_ENTERED } from "../../config/defaultValues";
import useDateValidation from "../../hooks/useDateValidation";


const DepartedForm = (props) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [error, setError] = useState(null);
  const { show, onClose, graveID, departedID, formType } = props;
  const bornDatePickerRef = useRef(null);
  const deathDatePickerRef = useRef(null);
  const [departedData, setDepartedData] = useState({
    uuid: "",
    graveID: graveID,
    namePrefix: "",
    familyName: "",
    bornFamilyName: "",
    givenName: "",
    bornYear: null,
    bornMonth: null,
    bornDay: null,
    deathYear: null,
    deathMonth: null,
    deathDay: null,
    birthPlaceID: null,
    deathPlaceID: null,
    occupationID: null,
    heroism: "",
    description: ""
  });
  const { setDatesIsChange } = useDateValidation(departedData, setDepartedData);


  const [attributes, setAttributes] = useState({
    places: [],
    occupations: [],
  });

  useEffect(() => {
    const getAttributes = async () => {
      try {
        const data = await getData("/departed/attributeLists");
        setAttributes(data);
      } catch (error) {
        setError(error);
      }
    };

    const fetchDepartedData = async () => {
      if (formType === "update" && departedID) {
        try {
          resetDepartedData();
          const departedData = await getData(`/departed/${departedID}`);
          setDepartedData({
            uuid: departedData.uuid,
            graveID: departedData?.graveID || null,
            namePrefix: departedData?.namePrefix || "",
            familyName: departedData?.familyName || "",
            bornFamilyName: departedData?.bornFamilyName || "",
            givenName: departedData?.givenName || "",
            bornYear: departedData?.bornYear || null,
            bornMonth: departedData?.bornMonth || null,
            bornDay: departedData?.bornDay || null,
            deathYear: departedData?.deathYear || null,
            deathMonth: departedData?.deathMonth || null,
            deathDay: departedData?.deathDay || null,
            birthPlaceID: departedData?.birthPlace?.id || null,
            deathPlaceID: departedData?.deathPlace?.id || null,
            occupationID: departedData?.occupation?.id || null,
            heroism: departedData?.heroism || "",
            description: departedData?.description || "",
          });
        } catch (error) {
          setError(error);
        }
      } else if (formType === "create") {
        resetDepartedData();
      }
    };

    getAttributes();
    fetchDepartedData();
  }, [formType, departedID]);

  const resetDepartedData = () => {
    setDepartedData({
      uuid: "",
      graveID: graveID,
      namePrefix: "",
      familyName: "",
      bornFamilyName: "",
      givenName: "",
      bornYear: null,
      bornMonth: null,
      bornDay: null,
      deathYear: null,
      deathMonth: null,
      deathDay: null,
      birthPlaceID: null,
      deathPlaceID: null,
      occupationID: null,
      heroism: "",
      description: ""
    });
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (
      [
        "bornYear",
        "bornMonth",
        "bornDay",
        "deathYear",
        "deathMonth",
        "deathDay",
        "birthPlaceID",
        "deathPlaceID",
        "occupationID",
      ].includes(name)
    ) {
      setDepartedData({ ...departedData, [name]: parseInt(value) });
    } else {
      setDepartedData({ ...departedData, [name]: value });
    }
  };

  const handleDateChange = (event) => {
    handleChange(event);
    setDatesIsChange(prevState => !prevState)
  }

  const handleSetDate = (event) => {
    const { name, value } = event.target;
    const date = value.split("-").map((value) => parseInt(value));
    if (name === "bornDate") {
      setDepartedData({
        ...departedData,
        bornYear: date[0],
        bornMonth: date[1],
        bornDay: date[2]
      })
    } else if (name === "deathDate") {
      setDepartedData({
        ...departedData,
        deathYear: date[0],
        deathMonth: date[1],
        deathDay: date[2]
      })
    }
    setDatesIsChange(prevState => !prevState)
  }


  const dateForPicker = (dateType) => {
    const curentDate = new Date();
    var year = curentDate.getFullYear();
    var month = 0;
    var day = 1;

    if (dateType === "bornDate") {
      if (!!departedData?.bornYear && departedData?.bornYear < 3000) year = departedData?.bornYear;
      if (!!departedData?.bornMonth) month = departedData?.bornMonth - 1;
      if (!!departedData?.bornDay) day = departedData?.bornDay;
    } else if (dateType === "deathDate") {
      if (!!departedData?.deathYear && departedData?.bornYear < 3000) year = departedData?.deathYear;
      if (!!departedData?.deathMonth) month = departedData?.deathMonth - 1;
      if (!!departedData?.deathDay) day = departedData?.deathDay;
    }

    const dateNow = new Date(year, month, day);
    dateNow.setMilliseconds(dateNow.getMilliseconds() + 20000000)
    return dateNow.toISOString().slice(0, 10);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formType === "create") {
        await axiosPrivate.post(
          "/departed",
          JSON.stringify(departedData)
        );

      } else if (formType === "update") {
        await axiosPrivate.put(
          "/departed",
          JSON.stringify(departedData)
        )
      }
      onClose(true);
    } catch (error) {
      alert(`${t("formSubmitFailedMessage") + error.message}`);
      console.error("Error submitting form: ", error);
    }
  };

  const renderBornDateForm = () => {
    return (<>
      <Row className="mb-3 modal-date-row">
        <Col className="my-1">
          <Form.Group as={Col} controlId="bornYear">
            <FloatingLabel
              controlId="bornYear"
              label={t("bornYear")}>
              <Form.Control
                name="bornYear"
                value={(departedData.bornYear) ? (departedData.bornYear) : ("")}
                onChange={handleDateChange}
                type="number"
                min={MINIMUM_YEAR_THAT_CAN_BE_ENTERED}
                max={new Date().getFullYear()}
                placeholder={""}
                className={"border-0 modal-background-color"}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
        <Col className="my-1">
          <Form.Group as={Col} controlId="bornMonth">
            <FloatingLabel
              controlId="bornMonth"
              label={t("bornMonth")}>
              <Form.Control
                name="bornMonth"
                value={(departedData.bornMonth) ? (departedData.bornMonth) : ("")}
                onChange={handleDateChange}
                type="number"
                min={1}
                max={12}
                placeholder={""}
                className={"border-0 modal-background-color"}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
        <Col className="my-1">
          <Form.Group as={Col} controlId="bornDay">
            <FloatingLabel
              controlId="bornDay"
              label={t("bornDay")}>
              <Form.Control
                name="bornDay"
                value={(departedData.bornDay) ? (departedData.bornDay) : ("")}
                onChange={handleDateChange}
                type="number"
                min={1}
                max={31}
                placeholder={""}
                className={"border-0 modal-background-color"}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
      </Row>
      <Col
        className="my-1 position-absolute date-selector-display"
        style={{ top: "180px", left: "calc(100% - 58px - 1rem)" }}
      >
        <Form.Group as={Col} controlId="bornDate">
          <Form.Control
            ref={bornDatePickerRef}
            name="bornDate"
            value={dateForPicker("bornDate")}
            onChange={handleSetDate}
            type="date"
            min={new Date(MINIMUM_YEAR_THAT_CAN_BE_ENTERED, 0, 2).toISOString().slice(0, 10)}
            max={new Date().toISOString().slice(0, 10)}
            className={"border-0 p-0 modal-background-color"}
            style={{ height: "63px", width: "0px", backgroundColor: "transparent", position: "absolute" }}
          />
          <Button
            variant={"outline-dark"}
            className={"shadow border-0 p-1"}
            style={{ height: "58px", width: "58px", fontSize: "x-large" }}
            onClick={() => bornDatePickerRef?.current?.showPicker()}>
            <i className="bi bi-calendar-event"></i>
          </Button>
        </Form.Group>
      </Col>
    </>)
  }

  const renderDeathDateForm = () => {
    return (<>
      <Row className="mb-3 modal-date-row">
        <Col className="my-1">
          <Form.Group as={Col} controlId="deathYear">
            <FloatingLabel
              controlId="deathYear"
              label={t("deathYear")}>
              <Form.Control
                name="deathYear"
                value={(departedData.deathYear) ? (departedData.deathYear) : ("")}
                onChange={handleDateChange}
                type="number"
                min={MINIMUM_YEAR_THAT_CAN_BE_ENTERED}
                max={new Date().getFullYear()}
                placeholder={""}
                className={"border-0 modal-background-color"}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
        <Col className="my-1">
          <Form.Group as={Col} controlId="deathMonth">
            <FloatingLabel
              controlId="deathMonth"
              label={t("deathMonth")}>
              <Form.Control
                name="deathMonth"
                value={(departedData.deathMonth) ? (departedData.deathMonth) : ("")}
                onChange={handleDateChange}
                type="number"
                min={1}
                max={12}
                placeholder={""}
                className={"border-0 modal-background-color"}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
        <Col className="my-1">
          <Form.Group as={Col} controlId="deathDay">
            <FloatingLabel
              controlId="deathDay"
              label={t("deathDay")}>
              <Form.Control
                name="deathDay"
                value={(departedData.deathDay) ? (departedData.deathDay) : ("")}
                onChange={handleDateChange}
                type="number"
                min={1}
                max={31}
                placeholder={""}
                className={"border-0 modal-background-color"}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
      </Row>
      <Col
        className="my-1 position-absolute date-selector-display"
        style={{ top: "262px", left: "calc(100% - 58px - 1rem)" }}
      >
        <Form.Group as={Col} controlId="deathDate">
          <Form.Control
            name="deathDate"
            value={dateForPicker("deathDate")}
            onChange={handleSetDate}
            type="date"
            ref={deathDatePickerRef}
            min={new Date(MINIMUM_YEAR_THAT_CAN_BE_ENTERED, 0, 2).toISOString().slice(0, 10)}
            max={new Date().toISOString().slice(0, 10)}
            className={"border-0 p-0 modal-background-color"}
            style={{ height: "63px", width: "0px", backgroundColor: "transparent", position: "absolute" }}
          />
          <Button
            variant={"outline-dark"}
            className={"shadow border-0 p-1 position-relative"}
            style={{ height: "58px", width: "58px", fontSize: "x-large" }}
            onClick={() => deathDatePickerRef?.current?.showPicker()}>
            <i className="bi bi-calendar-event"></i>
          </Button>
        </Form.Group>
      </Col>
    </>)
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {formType === "create" ? t("createDeparted") : t("updateDeparted")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="familyName">
                <FloatingLabel
                  controlId="familyName"
                  label={t("familyName")}>
                  <Form.Control
                    name="familyName"
                    value={departedData.familyName}
                    onChange={handleChange}
                    type="text"
                    placeholder={""}
                    className={"border-0 modal-background-color"}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="my-1">
              <Form.Group as={Col} controlId="bornFamilyName">
                <FloatingLabel
                  controlId="bornFamilyName"
                  label={t("bornFamilyName")}>
                  <Form.Control
                    name="bornFamilyName"
                    value={departedData.bornFamilyName}
                    onChange={handleChange}
                    type="text"
                    placeholder={""}
                    className={"border-0 modal-background-color"}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="my-1">
              <Form.Group as={Col} controlId="givenName">
                <FloatingLabel
                  controlId="givenName"
                  label={t("givenName")}>
                  <Form.Control
                    name="givenName"
                    value={departedData.givenName}
                    onChange={handleChange}
                    type="text"
                    placeholder={""}
                    className={"border-0 modal-background-color"}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <FloatingLabel
                controlId="floatingInput"
                label={t("namePrefix")}>
                <Form.Control
                  name="namePrefix"
                  value={departedData.namePrefix}
                  onChange={handleChange}
                  type="text"
                  placeholder={""}
                  className={"border-0 modal-background-color"}
                />
              </FloatingLabel>
            </Col>
            <Col></Col>
          </Row>
          {renderBornDateForm()}
          {renderDeathDateForm()}
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="birthPlace">
                <FloatingLabel
                  controlId="birthPlace"
                  label={t("birthPlace")}>
                  <Form.Select
                    name="birthPlaceID"
                    value={(departedData.birthPlaceID) ? (departedData.birthPlaceID) : (0)}
                    onChange={handleChange}
                    className={"border-0 modal-background-color"}
                  >
                    <option value={0}>{t("choose")}</option>
                    {attributes.places.map((place) => (
                      <option key={place.id} value={place.id}>
                        {place.placeName}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="my-1">
              <Form.Group as={Col} controlId="deathPlace">
                <FloatingLabel
                  controlId="deathPlace"
                  label={t("deathPlace")}>
                  <Form.Select
                    name="deathPlaceID"
                    value={(departedData.deathPlaceID) ? (departedData.deathPlaceID) : (0)}
                    onChange={handleChange}
                    className={"border-0 modal-background-color"}
                  >
                    <option value={0}>{t("choose")}</option>
                    {attributes.places.map((place) => (
                      <option key={place.id} value={place.id}>
                        {place.placeName}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="occupation">
                <FloatingLabel
                  controlId="occupation"
                  label={t("occupation")}>
                  <Form.Select
                    name="occupationID"
                    value={(departedData.occupationID) ? (departedData.occupationID) : (0)}
                    onChange={handleChange}
                    className={"border-0 modal-background-color"}
                  >
                    <option value={0}>{t("choose")}</option>
                    {attributes.occupations.map((occupation) => (
                      <option key={occupation.id} value={occupation.id}>
                        {occupation.occupationName}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="my-1">
              <Form.Group as={Col} controlId="heroism">
                <FloatingLabel
                  controlId="heroism"
                  label={t("heroism")}>
                  <Form.Control
                    name="heroism"
                    value={departedData.heroism}
                    onChange={handleChange}
                    type="text"
                    placeholder={""}
                    className={"border-0 modal-background-color"}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row className="modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="description">
                <Form.Label>{t("description")}</Form.Label>
                <Form.Control
                  name="description"
                  value={departedData.description}
                  onChange={handleChange}
                  as="textarea"
                  rows={8}
                  className={"border-0 modal-background-color"}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
        {error && <div className="text-danger">{error.message}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-dark"
          onClick={onClose}
          className={"shadow border-0 p-2 px-5"}
        >
          {t("close")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          className={"shadow border-0 p-2 px-5"}
        >
          {t("submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DepartedForm;
